import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { Star, Truck, ShieldCheck, BadgeCheck, Clock, Receipt, Award, ChevronRight, Calendar as CalendarIcon, CheckCircle, Sparkles, Shield, Users, Wrench, Phone, ArrowRight } from 'lucide-react';
import { carBrands, carModels, features, bangaloreLocations } from '../mock/mockData';
import { serviceService, testimonialService, bookingService } from '../services/api';
import { toast } from '../hooks/use-toast';
import ChatbotSection from '../components/ChatbotSection';
import { LocalBusinessSchema, OrganizationSchema, FAQSchema } from '../components/SchemaMarkup';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', mobile: '', carBrand: '', carModel: '', fuelType: '',
    manufacturingYear: '', service: '', address: '', location: '', date: null, timeSlot: '', specialRequirements: ''
  });
  
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [bookingId, setBookingId] = useState('');
  const [availableBrands, setAvailableBrands] = useState(carBrands);
  const [availableModels, setAvailableModels] = useState([]);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);

  const allTimeSlots = ['09:00 AM - 11:00 AM', '11:00 AM - 01:00 PM', '01:00 PM - 03:00 PM', '03:00 PM - 05:00 PM', '05:00 PM - 07:00 PM'];

  const getSlotStartHour = (slot) => {
    const timeStr = slot.split('-')[0].trim();
    const [time, meridiem] = timeStr.split(' ');
    let [hours, minutes] = time.split(':').map(Number);
    if (meridiem === 'PM' && hours !== 12) hours += 12;
    if (meridiem === 'AM' && hours === 12) hours = 0;
    return hours + minutes / 60;
  };

  const isAfternoonSlot = (slot) => getSlotStartHour(slot) >= 13;

  const getAvailableTimeSlots = () => {
    if (!formData.date) return allTimeSlots;
    const now = new Date();
    const selectedDate = new Date(formData.date);
    const currentHour = now.getHours();
    const currentMinutes = now.getMinutes();
    const currentTimeInHours = currentHour + currentMinutes / 60;
    const isToday = selectedDate.toDateString() === now.toDateString();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const isTomorrow = selectedDate.toDateString() === tomorrow.toDateString();
    if (isToday) return allTimeSlots.filter(slot => getSlotStartHour(slot) >= currentTimeInHours + 4);
    if (isTomorrow && currentHour >= 20) return allTimeSlots.filter(slot => isAfternoonSlot(slot));
    return allTimeSlots;
  };

  const timeSlots = getAvailableTimeSlots();

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchData();
    fetchVehicles();
  }, []);

  useEffect(() => {
    if (formData.date && formData.timeSlot) {
      const availableSlots = getAvailableTimeSlots();
      if (!availableSlots.includes(formData.timeSlot)) {
        setFormData(prev => ({ ...prev, timeSlot: '' }));
      }
    }
  }, [formData.date]);

  const fetchVehicles = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/vehicles`);
      const data = await response.json();
      const vehicles = data.vehicles || [];
      const brands = [...new Set(vehicles.map(v => v.brand))].sort();
      setAvailableBrands(brands);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    }
  };

  useEffect(() => {
    if (formData.carBrand) {
      const fetchModels = async () => {
        try {
          const response = await fetch(`${BACKEND_URL}/api/vehicles`);
          const data = await response.json();
          const vehicles = data.vehicles || [];
          const models = vehicles.filter(v => v.brand === formData.carBrand).map(v => v.model).filter((m, i, s) => s.indexOf(m) === i).sort();
          setAvailableModels(models);
        } catch (error) {
          setAvailableModels(carModels[formData.carBrand] || []);
        }
      };
      fetchModels();
    }
  }, [formData.carBrand]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [servicesData, testimonialsData] = await Promise.all([
        serviceService.getAll(),
        testimonialService.getAll()
      ]);
      setServices(servicesData.services || []);
      setTestimonials(testimonialsData.testimonials || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      toast({ title: 'Invalid Email', description: 'Please enter a valid email address', variant: 'destructive' });
      return;
    }
    setSendingOtp(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/otp/send`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email })
      });
      const data = await response.json();
      if (data.success) {
        setOtpSent(true);
        setOtpTimer(60);
        toast({ title: 'OTP Sent!', description: `Verification code sent to ${formData.email}` });
        const timer = setInterval(() => {
          setOtpTimer((prev) => { if (prev <= 1) { clearInterval(timer); return 0; } return prev - 1; });
        }, 1000);
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to send OTP', variant: 'destructive' });
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      toast({ title: 'Invalid OTP', description: 'Please enter a valid 6-digit OTP', variant: 'destructive' });
      return;
    }
    setVerifyingOtp(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/otp/verify`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email, otp: otp })
      });
      const data = await response.json();
      if (data.success && data.verified) {
        setOtpVerified(true);
        setOtpSent(false);
        toast({ title: 'Email Verified!', description: 'Your email has been successfully verified' });
      } else {
        toast({ title: 'Verification Failed', description: 'Invalid OTP', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to verify OTP', variant: 'destructive' });
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.mobile || !formData.email || !formData.carBrand || !formData.carModel || !formData.service) {
      toast({ title: 'Missing Information', description: 'Please fill all required fields', variant: 'destructive' });
      return;
    }
    if (!otpVerified) {
      toast({ title: 'Email Not Verified', description: 'Please verify your email address', variant: 'destructive' });
      return;
    }
    try {
      setIsSubmitting(true);
      const bookingData = {
        name: formData.name, email: formData.email, mobile: formData.mobile, carBrand: formData.carBrand, carModel: formData.carModel,
        fuelType: formData.fuelType || '', manufacturingYear: formData.manufacturingYear ? parseInt(formData.manufacturingYear) : null,
        service: formData.service, package: { name: "Standard Service", price: 0 },
        date: formData.date ? format(formData.date, 'yyyy-MM-dd') : null, timeSlot: formData.timeSlot || null,
        location: formData.location || '', address: formData.address || '', specialRequirements: formData.specialRequirements || ''
      };
      const response = await bookingService.create(bookingData);
      if (response.success) {
        setBookingId(response.booking_id);
        setIsSubmitted(true);
        toast({ title: 'Success!', description: 'Your booking has been confirmed.' });
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({ name: '', email: '', mobile: '', carBrand: '', carModel: '', fuelType: '', manufacturingYear: '', service: '', address: '', location: '', date: null, timeSlot: '', specialRequirements: '' });
          setOtpSent(false); setOtp(''); setOtpVerified(false); setOtpTimer(0);
        }, 5000);
      }
    } catch (error) {
      toast({ title: 'Booking Failed', description: 'Please try again.', variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const faqData = [
    { question: "How is Propitstop different?", answer: "We're not a garage - we're your independent advisor. We personally review every quote and concern." },
    { question: "Do you review quotes from other garages?", answer: "Yes! We'll review it for free and tell you what's necessary vs optional." },
    { question: "What areas in Bangalore do you serve?", answer: "We provide car service across all of Bangalore with free pickup and drop service." },
    { question: "Do you offer pickup service?", answer: "Yes! We offer completely FREE pickup and drop service." }
  ];

  const valueProps = [
    { icon: Shield, title: "No Upselling", desc: "We protect you from unnecessary repairs" },
    { icon: Users, title: "Personal Oversight", desc: "Your car is personally monitored by experts" },
    { icon: Wrench, title: "12+ Years Exp", desc: "Premium car service expertise" },
    { icon: Phone, title: "24/7 Support", desc: "Always here when you need us" }
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      <LocalBusinessSchema />
      <OrganizationSchema />
      <FAQSchema faqs={faqData} />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-slate-950 pt-8 pb-12">
        {/* Animated gradient layer */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900"
          animate={{
            backgroundPosition: ['0% 0%', '100% 100%', '0% 0%'],
          }}
          transition={{ duration: 18, ease: 'linear', repeat: Infinity }}
          style={{ backgroundSize: '200% 200%' }}
        />

        {/* Floating radial glows */}
        <div className="absolute inset-0 opacity-40 pointer-events-none">
          <motion.div
            className="absolute top-10 -left-20 w-[36rem] h-[36rem] rounded-full bg-cyan-500/15 blur-3xl"
            animate={{ x: [0, 60, 0], y: [0, 40, 0] }}
            transition={{ duration: 14, ease: 'easeInOut', repeat: Infinity }}
          />
          <motion.div
            className="absolute -bottom-24 right-0 w-[32rem] h-[32rem] rounded-full bg-blue-500/15 blur-3xl"
            animate={{ x: [0, -50, 0], y: [0, -30, 0] }}
            transition={{ duration: 16, ease: 'easeInOut', repeat: Infinity }}
          />
        </div>

        {/* Grid overlay */}
        <div
          className="absolute inset-0 opacity-[0.07] pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
            maskImage: 'radial-gradient(ellipse 80% 60% at 50% 40%, black 40%, transparent 75%)',
            WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at 50% 40%, black 40%, transparent 75%)',
          }}
        />

        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full mb-6 relative overflow-hidden"
            >
              <span className="relative">
                <span className="absolute inset-0 rounded-full bg-cyan-400/40 blur animate-ping" />
                <Sparkles className="relative w-4 h-4 text-cyan-400" />
              </span>
              <span className="text-sm text-cyan-300 font-medium">Trusted by 5000+ Car Owners in Bangalore</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: 'easeOut', delay: 0.1 }}
              className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6 tracking-tight"
            >
              Your Car's{' '}
              <span className="relative inline-block">
                <span className="absolute inset-0 blur-2xl bg-gradient-to-r from-cyan-400/40 to-blue-400/40 -z-10" aria-hidden />
                <span className="relative text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 via-cyan-400 to-blue-400">
                  Trusted Advisor
                </span>
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: 'easeOut', delay: 0.25 }}
              className="text-lg md:text-xl text-slate-300 mb-10 max-w-2xl mx-auto leading-relaxed"
            >
              Not just another garage aggregator. We personally review every concern and quote, protecting you from upselling.
            </motion.p>

            {/* Value Props */}
            <motion.div
              initial="hidden"
              animate="show"
              variants={{
                hidden: { opacity: 0 },
                show: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.5 } }
              }}
              className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
            >
              {valueProps.map((prop, i) => (
                <motion.div
                  key={i}
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' } }
                  }}
                  whileHover={{ y: -4 }}
                  className="group relative bg-slate-900/40 backdrop-blur-md border border-slate-700/50 rounded-xl p-4 hover:border-cyan-500/40 transition-[border-color,box-shadow] duration-300 hover:shadow-[0_8px_25px_-10px_rgba(6,182,212,0.5)]"
                  data-testid={`value-prop-${i}`}
                >
                  <div className="relative inline-flex items-center justify-center mb-2">
                    <span className="absolute inset-0 rounded-full bg-cyan-400/20 blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <prop.icon className="relative w-6 h-6 text-cyan-400" strokeWidth={1.75} />
                  </div>
                  <h3 className="text-white font-semibold text-sm">{prop.title}</h3>
                  <p className="text-slate-400 text-xs mt-1">{prop.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>

      </section>

      {/* AI Chatbot */}
      <ChatbotSection />

      {/* Talk to Expert CTA Banner */}
      <section className="pt-10 pb-2 bg-slate-950">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <a
              href="tel:+919110416663"
              data-testid="talk-to-expert-banner"
              className="group relative flex flex-col sm:flex-row items-center justify-between gap-4 rounded-2xl border border-cyan-500/30 bg-gradient-to-r from-slate-900/80 via-blue-950/80 to-slate-900/80 backdrop-blur-xl px-6 py-5 overflow-hidden hover:border-cyan-500/60 transition-colors duration-300"
            >
              <div className="absolute inset-0 pointer-events-none opacity-60">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_50%,rgba(6,182,212,0.18),transparent_55%)]" />
              </div>
              <div className="absolute -top-px left-1/4 right-1/4 h-px bg-gradient-to-r from-transparent via-cyan-400/60 to-transparent" />

              <div className="relative flex items-center gap-4 text-center sm:text-left">
                <div className="relative hidden sm:flex w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/30 items-center justify-center flex-shrink-0">
                  <span className="absolute inset-0 rounded-xl bg-cyan-400/20 animate-ping opacity-75" />
                  <Phone className="relative w-5 h-5 text-cyan-400" strokeWidth={2} />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-cyan-400 font-medium">Speak with a human</p>
                  <p className="mt-0.5 text-lg md:text-xl font-extrabold text-white tracking-tight">
                    Talk to our Professional Car Experts Now
                  </p>
                </div>
              </div>

              <div className="relative flex items-center gap-2 px-5 py-3 rounded-xl bg-cyan-500 text-slate-950 font-bold shadow-[0_0_18px_rgba(6,182,212,0.35)] group-hover:shadow-[0_0_28px_rgba(6,182,212,0.55)] transition-[box-shadow] duration-300 whitespace-nowrap">
                <Phone className="w-4 h-4" strokeWidth={2.25} />
                <span className="text-base">Call 91104 16663</span>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-16 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-white mb-2">Book Your Service</h2>
              <p className="text-slate-400">Get a free expert review • Response within 2 hours</p>
            </div>
            
            {isSubmitted ? (
              <div className="bg-slate-800/50 backdrop-blur border border-green-500/30 rounded-2xl p-8 text-center">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Booking Confirmed!</h3>
                <p className="text-slate-300 mb-4">Booking ID: <span className="text-cyan-400 font-mono">{bookingId}</span></p>
                <p className="text-slate-400 text-sm">We'll contact you shortly</p>
              </div>
            ) : (
              <div className="bg-slate-900/50 backdrop-blur border border-slate-700/50 rounded-2xl p-6">
                <form onSubmit={handleBooking} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Full Name *</label>
                      <Input placeholder="Enter your name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Mobile *</label>
                      <Input type="tel" placeholder="Mobile number" value={formData.mobile} onChange={(e) => setFormData({ ...formData, mobile: e.target.value })} maxLength={10}
                        className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Email *</label>
                    <div className="flex gap-2">
                      <Input type="email" placeholder="Email address" value={formData.email} disabled={otpVerified}
                        onChange={(e) => { setFormData({ ...formData, email: e.target.value }); if (otpVerified) { setOtpVerified(false); setOtpSent(false); setOtp(''); } }}
                        className="flex-1 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500" />
                      {formData.email && !otpVerified && (
                        <Button type="button" onClick={handleSendOtp} disabled={sendingOtp || (otpSent && otpTimer > 0)}
                          className="bg-cyan-600 hover:bg-cyan-500 text-white">
                          {sendingOtp ? 'Sending...' : otpSent && otpTimer > 0 ? `${otpTimer}s` : 'Send OTP'}
                        </Button>
                      )}
                      {otpVerified && <div className="flex items-center text-green-400 px-3"><CheckCircle className="w-5 h-5 mr-1" /> Verified</div>}
                    </div>
                    {otpSent && !otpVerified && (
                      <div className="mt-3 p-3 bg-slate-800/50 border border-cyan-500/20 rounded-lg">
                        <div className="flex gap-2">
                          <Input type="text" placeholder="Enter 6-digit OTP" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))} maxLength={6}
                            className="flex-1 bg-slate-900/50 border-slate-700 text-white" />
                          <Button type="button" onClick={handleVerifyOtp} disabled={otp.length !== 6 || verifyingOtp} className="bg-cyan-600 hover:bg-cyan-500">
                            {verifyingOtp ? '...' : 'Verify'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Service *</label>
                    <Select value={formData.service} onValueChange={(value) => setFormData({ ...formData, service: value })}>
                      <SelectTrigger className="bg-slate-800/50 border-slate-700 text-white">
                        <SelectValue placeholder="Select service" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        {services.map(s => <SelectItem key={s.id} value={s.name} className="text-white hover:bg-slate-700">{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Car Brand *</label>
                      <Select value={formData.carBrand} onValueChange={(v) => setFormData({ ...formData, carBrand: v, carModel: '' })}>
                        <SelectTrigger className="bg-slate-800/50 border-slate-700 text-white">
                          <SelectValue placeholder="Brand" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700 max-h-[250px]">
                          {availableBrands.map(b => <SelectItem key={b} value={b} className="text-white hover:bg-slate-700">{b}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Model *</label>
                      <Select value={formData.carModel} onValueChange={(v) => setFormData({ ...formData, carModel: v })} disabled={!formData.carBrand}>
                        <SelectTrigger className="bg-slate-800/50 border-slate-700 text-white">
                          <SelectValue placeholder="Model" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700 max-h-[250px]">
                          {availableModels.map(m => <SelectItem key={m} value={m} className="text-white hover:bg-slate-700">{m}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Fuel Type</label>
                      <Select value={formData.fuelType} onValueChange={(v) => setFormData({ ...formData, fuelType: v })}>
                        <SelectTrigger className="bg-slate-800/50 border-slate-700 text-white">
                          <SelectValue placeholder="Fuel" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          {['Petrol', 'Diesel', 'CNG', 'Electric', 'Hybrid'].map(f => <SelectItem key={f} value={f} className="text-white hover:bg-slate-700">{f}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Location</label>
                      <Select value={formData.location} onValueChange={(v) => setFormData({ ...formData, location: v })}>
                        <SelectTrigger className="bg-slate-800/50 border-slate-700 text-white">
                          <SelectValue placeholder="Area" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700 max-h-[200px]">
                          {bangaloreLocations.map(l => <SelectItem key={l} value={l} className="text-white hover:bg-slate-700">{l}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Special Requirements</label>
                    <Textarea placeholder="Any specific concerns or requests" value={formData.specialRequirements} onChange={(e) => setFormData({ ...formData, specialRequirements: e.target.value })} rows={2}
                      className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500" />
                  </div>

                  <Button type="submit" disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white font-bold py-6 text-lg rounded-xl shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 transition-all">
                    {isSubmitting ? 'Submitting...' : 'Get Expert Review →'}
                  </Button>
                </form>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-slate-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Our Services</h2>
            <p className="text-slate-400 max-w-xl mx-auto">Comprehensive car care by certified experts</p>
          </div>
          
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {services.slice(0, 4).map((service, i) => (
                <div key={service.id} className="group bg-slate-900/50 backdrop-blur border border-slate-800 rounded-2xl overflow-hidden hover:border-cyan-500/30 transition-all hover:-translate-y-1">
                  <div className="h-40 overflow-hidden">
                    <img src={service.image} alt={service.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-5">
                    <h3 className="text-lg font-bold text-white mb-2">{service.name}</h3>
                    <p className="text-slate-400 text-sm mb-4 line-clamp-2">{service.description}</p>
                    <Link to={`/services?service=${service.id}`}>
                      <Button variant="ghost" className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10 p-0">
                        Learn More <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Why Choose Propitstop?</h2>
            <p className="text-slate-400">We deliver excellence in every service</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => {
              const icons = { Truck, ShieldCheck, BadgeCheck, Clock, Receipt, Award };
              const Icon = icons[feature.icon] || ShieldCheck;
              return (
                <div key={feature.id} className="bg-slate-900/30 backdrop-blur border border-slate-800 rounded-2xl p-6 hover:border-cyan-500/30 transition-all group">
                  <div className="w-12 h-12 bg-cyan-500/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-cyan-500/20 transition-colors">
                    <Icon className="w-6 h-6 text-cyan-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-slate-400 text-sm">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-slate-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">What Customers Say</h2>
            <p className="text-slate-400">Real experiences from real customers</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.slice(0, 4).map((t) => (
              <div key={t.id} className="bg-slate-900/30 backdrop-blur border border-slate-800 rounded-2xl p-6 hover:border-cyan-500/20 transition-all">
                <div className="flex gap-1 mb-3">
                  {[...Array(t.rating)].map((_, i) => <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />)}
                </div>
                <p className="text-slate-300 text-sm mb-4 line-clamp-3">"{t.comment}"</p>
                <div className="border-t border-slate-800 pt-3">
                  <p className="font-semibold text-white text-sm">{t.customer_name}</p>
                  <p className="text-slate-500 text-xs">{t.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Frequently Asked Questions</h2>
            </div>
            
            <div className="space-y-4">
              {faqData.map((faq, i) => (
                <div key={i} className="bg-slate-900/30 backdrop-blur border border-slate-800 rounded-xl p-5">
                  <h3 className="text-white font-semibold mb-2">{faq.question}</h3>
                  <p className="text-slate-400 text-sm">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-cyan-600 to-blue-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Ready to Service Your Car?</h2>
          <p className="text-cyan-100 mb-8 max-w-xl mx-auto">Get expert care for your vehicle with free pickup and drop service</p>
          <Link to="/service/services">
            <Button className="bg-white text-slate-900 hover:bg-slate-100 px-8 py-6 text-lg font-bold rounded-xl shadow-lg hover:shadow-xl transition-all">
              Book Service Now <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
